import { Data, Effect, identity } from 'effect'

import { CORE_CONSTANT } from '../../constant.ts'
import type {
  ViewQueryAggregation,
  ViewQueryRecordNode,
  ViewQueryResult,
  ViewQueryResultCursor,
} from '../../schemas/query/index.ts'
import {
  type AggregationQueryError,
  prefixErrorNameToMessage,
  SqlExecutionError,
  SqlGenerationError,
  SqlOverrideResolverError,
  wrapErrorOrDie,
} from './executeViewQuery/errors.ts'
import { generateAggregationSql, type NodePredicateResolver } from './executeViewQuery/generateSql.ts'
import { renderSql } from './executeViewQuery/renderSql.ts'

export type SqlResultRecord = Record<string, unknown>

export type ViewResultCursorOrInitialCursor = Omit<ViewQueryResultCursor, 'nextId'> & {
  nextId?: string | null
}

export class TooManyChildRecordsError extends Error {
  // eslint-disable-next-line unicorn/custom-error-definition
  public constructor(message: string) {
    super(message)
    this.name = 'TooManyChildRecordsError'
  }
}

type PromiseOrNonPromise<T> = Promise<T> | T
interface SqlClientLogBody {
  viewId: string
  viewName: string
}
export interface SqlClient {
  query: (
    sql: string,
    option?: { logBody?: SqlClientLogBody },
  ) => PromiseOrNonPromise<{
    rows: Array<Record<string, unknown>>
  }>
}

interface ExecuteAggregationQueryParameters {
  query: ViewQueryAggregation
  sqlClient: SqlClient
  mustacheParameter: Record<string, unknown>
  accountClosingMonth: number
  sqlOverrideResolver?: (sql: string) => Promise<string | undefined>
  resolveNodePredicate: NodePredicateResolver
  option?: {
    logBody?: SqlClientLogBody
    unlimitedRowsEnabled?: boolean
  }
}

interface AggregationQuerySuccess {
  rows: SqlResultRecord[]
  hasMoreRows: boolean
  sql: string
  originalRawSql: string
  overriddenRawSql: string | undefined
}

// executeAggregationQuery (Promise境界) の外部契約。Effect内部ではSQL実行の失敗は
// SqlExecutionErrorとして失敗チャンネルを流れるが、Promise境界では従来どおり
// 「成功レスポンスの error フィールドで返す」形に変換する (呼び出し元とテストが固定している契約。
// 経緯は executeViewQueryForAggregationQuery.test.ts 冒頭の設計ノートを参照)。
type AggregationQueryResult =
  | (AggregationQuerySuccess & { error?: undefined })
  | {
      rows: SqlResultRecord[]
      hasMoreRows?: undefined
      sql: string
      originalRawSql: string
      overriddenRawSql: string | undefined
      error: Error
    }

/**
 * 集計SQLを生成・実行するEffect。
 *
 * 失敗チャンネルは失敗経路ごとのタグ付きエラーの直和型 (定義は executeViewQuery/errors.ts)
 *
 * Error以外の値がthrowされた場合はタグに分類せずdefectとし、Effect.runPromiseの境界で
 * throw元の値のままrejectされる (従来の素通し挙動の維持)。
 */
export const executeAggregationQueryEffect = ({
  query,
  sqlClient,
  mustacheParameter,
  accountClosingMonth,
  sqlOverrideResolver,
  resolveNodePredicate,
  option,
}: ExecuteAggregationQueryParameters): Effect.Effect<AggregationQuerySuccess, AggregationQueryError> =>
  Effect.gen(function* () {
    const rawSql = yield* Effect.try({
      try: () =>
        generateAggregationSql(query, mustacheParameter, {
          resolveNodePredicate,
          unlimitedRowsEnabled: option?.unlimitedRowsEnabled,
        }),
      catch: identity,
    }).pipe(Effect.catch(wrapErrorOrDie((cause) => new SqlGenerationError({ cause }))))
    const overriddenRawSql =
      sqlOverrideResolver === undefined
        ? undefined
        : yield* Effect.tryPromise({ try: async () => await sqlOverrideResolver(rawSql), catch: identity }).pipe(
            Effect.catch(wrapErrorOrDie((cause) => new SqlOverrideResolverError({ cause }))),
          )
    const sql = yield* Effect.try({
      try: () => renderSql(overriddenRawSql ?? rawSql, mustacheParameter, { accountClosingMonth }),
      catch: identity,
    }).pipe(Effect.catch(wrapErrorOrDie((cause) => new SqlGenerationError({ cause }))))

    return yield* Effect.tryPromise({
      try: async () => {
        const result = await sqlClient.query(sql, option)
        const hasMoreRows =
          option?.unlimitedRowsEnabled !== true && result.rows.length > CORE_CONSTANT.AGGREGATION_QUERY_LIMIT
        return {
          rows: hasMoreRows ? result.rows.slice(0, CORE_CONSTANT.AGGREGATION_QUERY_LIMIT) : result.rows,
          hasMoreRows,
        }
      },
      catch: identity,
    }).pipe(
      Effect.map(({ rows, hasMoreRows }): AggregationQuerySuccess => ({
        rows,
        hasMoreRows,
        sql,
        originalRawSql: rawSql,
        overriddenRawSql,
      })),
      Effect.catch(
        wrapErrorOrDie(
          (cause) =>
            new SqlExecutionError({
              cause: prefixErrorNameToMessage(cause),
              sql,
              originalRawSql: rawSql,
              overriddenRawSql,
            }),
        ),
      ),
    )
  })

export const executeAggregationQuery = async (
  parameters: ExecuteAggregationQueryParameters,
): Promise<AggregationQueryResult> =>
  await Effect.runPromise(
    executeAggregationQueryEffect(parameters).pipe(
      // SQL実行の失敗はソフトな失敗であり、本関数の呼び出し元にその処遇を委ねるため
      // ここで成功チャネルへ載せ替えて Promise を正常に resolve させる
      Effect.catchTag('SqlExecutionError', (error): Effect.Effect<AggregationQueryResult> =>
        Effect.succeed({
          rows: [],
          sql: error.sql,
          originalRawSql: error.originalRawSql,
          overriddenRawSql: error.overriddenRawSql,
          error: error.cause,
        }),
      ),
      // SQL生成・sqlOverrideResolverの失敗はタグを剥がして元エラーに戻し、従来どおり呼び出し元へthrowする。
      // Effect.mapErrorを使う理由はexecuteViewQueryForMultiTablePivotの境界と同じ
      Effect.mapError((error) => error.cause),
    ),
  )

type ExecuteViewQueryForAggregationQueryParameters = Omit<ExecuteAggregationQueryParameters, 'option'>

// groupByCurrentNode (結果ツリー組み立て) の失敗。executeViewQueryForAggregationQueryEffect の
// 内部で正常レスポンス化されて外に出ないため、共有タグ群 (executeViewQuery/errors.ts) には置かない
class RecordNodeGroupingError extends Data.TaggedError('RecordNodeGroupingError')<{ cause: Error }> {}

const executeViewQueryForAggregationQueryEffect = ({
  query,
  sqlClient,
  mustacheParameter,
  accountClosingMonth,
  sqlOverrideResolver,
  resolveNodePredicate,
}: ExecuteViewQueryForAggregationQueryParameters): Effect.Effect<ViewQueryResult> => {
  const createErrorViewQueryResult = (message: string): ViewQueryResult => ({
    viewQuery: query,
    result: [],
    nextCursor: undefined,
    error: message,
  })

  return Effect.gen(function* () {
    const { rows, sql, originalRawSql, overriddenRawSql } = yield* executeAggregationQueryEffect({
      query,
      sqlClient,
      mustacheParameter,
      accountClosingMonth,
      sqlOverrideResolver,
      resolveNodePredicate,
    })
    // 結果ツリー組み立てのErrorも従来どおり正常レスポンス化の対象 (下のcatchTagsで変換する)
    const nodeRecords = yield* Effect.try({ try: () => groupByCurrentNode(rows), catch: identity }).pipe(
      Effect.catch(wrapErrorOrDie((cause) => new RecordNodeGroupingError({ cause }))),
    )

    return {
      viewQuery: query,
      result: nodeRecords,
      sqls: [sql],
      sqlDetails: [
        {
          sql,
          originalRawSql,
          overriddenRawSql,
          mustacheParameter,
          // eslint-disable-next-line sonarjs/todo-tag
          name: `集計SQL`, // TODO
        },
      ],
      nextCursor: undefined,
    }
  }).pipe(
    // aggregation単体は全タグを正常レスポンス化する (multiTablePivotとは非対称な契約。
    // 全タグをcatchしていることは残余エラー型がneverに縮むことで型検査される)。
    // SqlExecutionErrorのcause.messageは`${name}: ${message}`形式に書き換え済みで、
    // 他タグは生のmessageのまま (この差もテストで固定された外部契約)
    Effect.catchTags({
      SqlExecutionError: (error) => Effect.succeed(createErrorViewQueryResult(error.cause.message)),
      SqlGenerationError: (error) => Effect.succeed(createErrorViewQueryResult(error.cause.message)),
      SqlOverrideResolverError: (error) => Effect.succeed(createErrorViewQueryResult(error.cause.message)),
      RecordNodeGroupingError: (error) =>
        Effect.succeed(
          error.cause instanceof TooManyChildRecordsError
            ? createErrorViewQueryResult(`子レコードが多すぎます。フィールド設定よりツリーを見直してください。`)
            : createErrorViewQueryResult(error.cause.message),
        ),
    }),
  )
}

/**
 * シートのフッター集計 (列の右クリックメニューから設定する集計) で使われるクエリの実行関数。
 *
 * `executeViewQueryForListQuery` と同じく SQL を生成・実行して `RecordNode` を返します。
 * 集計クエリのレスポンスをツリー構造で表現する意味は薄いのですが、後続処理の都合で
 * `RecordNode` の形に揃えています (= ツリー化処理はやや実装サボり気味)。
 */
export const executeViewQueryForAggregationQuery = async (
  parameters: ExecuteViewQueryForAggregationQueryParameters,
): Promise<ViewQueryResult> => await Effect.runPromise(executeViewQueryForAggregationQueryEffect(parameters))

function groupByCurrentNode(records: SqlResultRecord[]): ViewQueryRecordNode[] {
  if (records.length <= 1) {
    const [record] = records
    if (record === undefined) {
      return []
    }

    return [
      {
        id: CORE_CONSTANT.AGGREGATION_NO_GROUPING_ROOT_ID,
        attributes: record,
        meta: {
          height: 1,
          innerRowIndexStart: 0,
          innerRowIndexEnd: 1,
        },
        children: [],
      },
    ]
  }

  return records.map((record, index) => ({
    id: `group-${index + 1}`,
    attributes: record,
    meta: {
      height: 1,
      innerRowIndexStart: index,
      innerRowIndexEnd: index + 1,
    },
    children: [],
  }))
}
